import React, { useState, useEffect } from 'react'
import { Card, Input, Button, Table, Switch, Tag, Typography, message, Image, Space } from 'antd'
import { CloudServerOutlined, UploadOutlined, PictureOutlined } from '@ant-design/icons'
import api, { backend } from '../../api'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

export default function VersionManage() {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [version, setVersion] = useState('')
  const [downloadUrl, setDownloadUrl] = useState('')
  const [changelog, setChangelog] = useState('')
  const [fileSize, setFileSize] = useState('')
  const [sha256, setSha256] = useState('')
  const [forceUpdate, setForceUpdate] = useState(false)

  const [appName, setAppName] = useState('')
  const [appLogo, setAppLogo] = useState('')
  const [savingConfig, setSavingConfig] = useState(false)

  useEffect(() => {
    api.getAppConfig().then(cfg => {
      if (cfg) {
        setAppName(cfg.appName || '')
        setAppLogo(cfg.appLogo || '')
      }
    })
  }, [])

  const handleSelectLogo = async () => {
    const result = await api.selectFile({ title: '选择软件 Logo', filters: [{ name: '图片文件', extensions: ['png', 'jpg', 'jpeg', 'svg', 'ico'] }] })
    if (result.canceled || !result.path) return
    const fileResult = await api.readFileAsBase64(result.path)
    if (fileResult.ok) setAppLogo(fileResult.data)
  }

  const handleSaveAppConfig = async () => {
    setSavingConfig(true)
    try {
      const res = await api.saveAppConfig({ appName, appLogo })
      if (res.ok) message.success('软件配置已保存')
      else message.error(res.message || '保存失败')
    } catch (err) {
      message.error('保存失败: ' + (err.message || '未知错误'))
    } finally {
      setSavingConfig(false)
    }
  }

  const fetchVersions = async () => {
    setLoading(true)
    try {
      const res = await backend.adminGetVersions()
      if (res.versions) {
        setVersions(res.versions)
      } else if (Array.isArray(res)) {
        setVersions(res)
      } else {
        setVersions([])
      }
    } catch (err) {
      message.error('获取版本列表失败: ' + (err.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchVersions() }, [])

  const handleUpload = async () => {
    if (!version.trim()) { message.warning('请输入版本号'); return }
    if (!downloadUrl.trim()) { message.warning('请输入下载地址'); return }
    setUploading(true)
    try {
      const res = await backend.adminUploadVersion({
        version: version.trim(),
        download_url: downloadUrl.trim(),
        changelog: changelog.trim(),
        file_size: Number(fileSize || 0),
        sha256: sha256.trim(),
        force_update: forceUpdate
      })
      if (res.success) {
        message.success('版本发布成功')
        setVersion('')
        setDownloadUrl('')
        setChangelog('')
        setFileSize('')
        setSha256('')
        setForceUpdate(false)
        fetchVersions()
      } else {
        message.error(res.message || '发布失败')
      }
    } catch (err) {
      message.error('发布失败: ' + (err.message || '未知错误'))
    } finally {
      setUploading(false)
    }
  }

  const columns = [
    {
      title: '版本号', dataIndex: 'version', key: 'version', width: 120
    },
    {
      title: '更新日志', dataIndex: 'changelog', key: 'changelog',
      ellipsis: true,
      render: v => <span title={v}>{v || '-'}</span>
    },
    {
      title: '强制更新', dataIndex: 'force_update', key: 'force_update', width: 100,
      render: v => <Tag color={v ? 'red' : 'default'}>{v ? '是' : '否'}</Tag>
    },
    {
      title: '文件大小', dataIndex: 'file_size', key: 'file_size', width: 110,
      render: v => v ? `${(Number(v) / 1024 / 1024).toFixed(2)} MB` : '-'
    },
    {
      title: '发布时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: v => v ? new Date(v).toLocaleString() : '-'
    }
  ]

  return (
    <div>
      <Title level={4}><CloudServerOutlined /> 版本管理</Title>

      <Card title={<span><PictureOutlined /> 软件名称与 Logo</span>} style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13 }}>软件名称</Text>
          <Input
            value={appName}
            onChange={e => setAppName(e.target.value)}
            placeholder="例如: 矩阵管理系统"
            style={{ marginTop: 4, maxWidth: 400 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: 13 }}>软件 Logo</Text>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button icon={<UploadOutlined />} onClick={handleSelectLogo}>选择 Logo 图片</Button>
            {appLogo && (
              <Image src={appLogo} width={48} height={48} style={{ borderRadius: 8, objectFit: 'contain' }} preview={{ mask: '预览' }} />
            )}
          </div>
        </div>
        <Button type="primary" loading={savingConfig} onClick={handleSaveAppConfig}>
          保存软件配置
        </Button>
      </Card>

      <Card title="上传新版本" style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13 }}>版本号</Text>
          <Input
            value={version}
            onChange={e => setVersion(e.target.value)}
            placeholder="例如: 2.1.0"
            style={{ marginTop: 4, maxWidth: 300 }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13 }}>下载地址</Text>
          <Input
            value={downloadUrl}
            onChange={e => setDownloadUrl(e.target.value)}
            placeholder="请输入版本下载链接"
            style={{ marginTop: 4, maxWidth: 500 }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13 }}>更新日志</Text>
          <TextArea
            value={changelog}
            onChange={e => setChangelog(e.target.value)}
            placeholder="请输入版本更新日志"
            rows={4}
            maxLength={2000}
            showCount
            style={{ marginTop: 4, maxWidth: 600 }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13 }}>文件大小（字节）</Text>
          <Input
            value={fileSize}
            onChange={e => setFileSize(e.target.value.replace(/\D/g, ''))}
            placeholder="例如: 104857600"
            style={{ marginTop: 4, maxWidth: 300 }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text style={{ fontSize: 13 }}>SHA256 校验值</Text>
          <Input
            value={sha256}
            onChange={e => setSha256(e.target.value.trim())}
            placeholder="请输入安装包 SHA256，可留空"
            style={{ marginTop: 4, maxWidth: 600 }}
          />
        </div>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 13 }}>强制更新</Text>
          <Switch checked={forceUpdate} onChange={setForceUpdate} />
        </div>
        <Button type="primary" icon={<UploadOutlined />} loading={uploading} onClick={handleUpload}>
          上传版本
        </Button>
      </Card>

      <Card title="版本历史">
        <Table
          rowKey={r => r.id || r.version}
          columns={columns}
          dataSource={versions}
          loading={loading}
          scroll={{ x: 600 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `共 ${t} 条` }}
        />
      </Card>
    </div>
  )
}
